<template>
  <div class="image-generation">
    <ImageProductSetup
      :image-url="imageUrl"
      @image-uploaded="handleImageUploaded"
      @generate-image="handleGenerateImage"
    />

    <ImageGeneratedCreative
      ref="imageGeneratedCreative"
      :images="images"
      @generate-video="handleGenerateVideo"
    />

    <VideoSource
      ref="videoSource"
      @next="handleSelectedVideo"
      @video-polling-complete="handleVideoPollingComplete"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ImageProductSetup from './ImageProductSetup.vue'
import VideoSource from './VideoSource.vue'
import ImageGeneratedCreative from './ImageGeneratedCreative.vue'

export default {
  name: 'ImageGeneration',
  components: {
    ImageProductSetup,
    VideoSource,
    ImageGeneratedCreative,
  },
  data() {
    return {
      imageUrl: '',
      images: [],
      currentGeneratingCardId: null,
    }
  },
  computed: {
    ...mapGetters('videoGeneration', [
      'generatedImageUrl',
      'generatedImagePromptText',
      'imageGenerationPollingError',
    ]),
  },
  watch: {
    generatedImagePromptText(newPromptText) {
      // This watcher is triggered when the prompt is generated (after the image URL is set)
      if (this.generatedImageUrl && newPromptText) {
        this.addGeneratedImage(this.generatedImageUrl, newPromptText)
      }
    },
    imageGenerationPollingError(error) {
      if (error) {
        // Remove the generating card when polling fails
        if (this.currentGeneratingCardId) {
          this.removeImageFromGrid(this.currentGeneratingCardId)
          this.currentGeneratingCardId = null
        }
        // Show error notification
        this.$toast.error(error)
      }
    },
  },
  mounted() {
    this.$emit('image-state-change', Boolean(this.imageUrl))
  },
  methods: {
    ...mapActions('videoGeneration', ['generateVideo', 'generateImageFromImage', 'startImageGenerationPolling']),
    setImageUrl(url) {
      if (this.imageUrl === url) return
      this.imageUrl = url
      this.$emit('image-state-change', Boolean(url))
    },
    handleImageUploaded(url) {
      this.setImageUrl(url)
      // Replace the existing original image or add new one if none exists
      this.replaceOrAddOriginalImage(url)
    },
    async handleGenerateImage(payload) {
      let generatingCardId = null

      try {
        const { prompt, promptIds, imageUrl } = payload

        // Clear any previous error state
        this.$store.commit('videoGeneration/POLL_IMAGE_GENERATION_SUCCESS', null)

        // Add generating card immediately
        generatingCardId = `generating-${Date.now()}`
        this.addImageToGrid({
          id: generatingCardId,
          url: imageUrl, // Show the source image while generating
          badgeLabel: '', // Generating...
          prompt,
          isGenerating: true,
          isLoadingPrompt: false,
          isGeneratingVideo: false,
        })

        // Store the generating card ID for later updates
        this.currentGeneratingCardId = generatingCardId

        // Prepare the API payload
        const apiPayload = {
          sourceImageUrls: [imageUrl],
        }

        // Use promptIds if available, otherwise fall back to prompt text
        if (promptIds && promptIds.length > 0) {
          apiPayload.promptIds = promptIds
        } else if (prompt) {
          apiPayload.prompt = prompt
        }

        // Call the image generation API
        const result = await this.generateImageFromImage(apiPayload)

        if (result.success && result.data?.result?._id) { // eslint-disable-line no-underscore-dangle
          // Start polling for the generated image
          await this.startImageGenerationPolling(result.data.result._id) // eslint-disable-line no-underscore-dangle
        } else {
          // Remove generating card if API call failed
          if (generatingCardId) {
            this.removeImageFromGrid(generatingCardId)
          }
          this.$toast.error('Failed to start image generation')
        }
      } catch (error) {
        // Remove generating card if error occurred
        if (generatingCardId) {
          this.removeImageFromGrid(generatingCardId)
        }
        this.$toast.error('Failed to generate image')
        // eslint-disable-next-line no-console
        console.error('[ImageGeneration] generateImageFromImage failed', error)
      }
    },
    handleGenerateVideo(payload) {
      const taskId = payload?.taskId || payload?.response?.data?.[0]?.taskId || ''
      if (!taskId) {
        this.$toast.error('Failed to start video generation')
        return
      }

      const videoCard = {
        id: taskId,
        taskId,
        status: 'Processing',
        prompt: payload?.prompt || '',
        imageUrl: payload?.imageUrl || this.imageUrl,
        videoUrl: '',
        createdAt: new Date().toISOString(),
      }

      const videoSourcePanel = this.$refs.videoSource
      if (videoSourcePanel && typeof videoSourcePanel.addVideo === 'function') {
        videoSourcePanel.addVideo(videoCard)
      }
    },
    handleVideoPollingComplete(payload) {
      // Reset the generating state for the image card when video polling completes
      const { imageGeneratedCreative } = this.$refs
      if (imageGeneratedCreative && typeof imageGeneratedCreative.resetGeneratingState === 'function') {
        // Find the image that was generating the video by matching taskId
        const generatingImage = this.images.find(img => img.videoTaskId === payload.taskId)
        if (generatingImage) {
          imageGeneratedCreative.resetGeneratingState(generatingImage.id)
        }
      }
    },
    handleSelectedVideo(selectedVideos) {
      this.$emit('next', selectedVideos)
    },
    addImageToGrid(imageData) {
      this.images.push(imageData)
    },
    replaceOrAddOriginalImage(url) {
      // Find existing original image
      const originalIndex = this.images.findIndex(img => img.badgeLabel === 'Original')

      const originalImageData = {
        id: `original-${Date.now()}`,
        url,
        badgeLabel: 'Original',
        prompt: '',
        isGenerating: false,
        isLoadingPrompt: false,
        isGeneratingVideo: false,
      }

      if (originalIndex > -1) {
        // Replace existing original image
        this.$set(this.images, originalIndex, originalImageData)
      } else {
        // Add new original image if none exists
        this.addImageToGrid(originalImageData)
      }
    },
    removeImageFromGrid(imageId) {
      const index = this.images.findIndex(img => img.id === imageId)
      if (index > -1) {
        this.images.splice(index, 1)
      }
    },
    updateImageGeneratingState(imageId, isGenerating) {
      const index = this.images.findIndex(img => img.id === imageId)
      if (index > -1) {
        this.$set(this.images[index], 'isGenerating', isGenerating)
      }
    },
    addGeneratedImage(imageUrl, promptText) {
      // Replace the current generating card with the generated image
      if (this.currentGeneratingCardId) {
        const index = this.images.findIndex(img => img.id === this.currentGeneratingCardId)
        if (index > -1) {
          // Update the existing card with the generated image data
          const updatedCard = {
            ...this.images[index],
            url: imageUrl,
            badgeLabel: 'Generated',
            prompt: promptText || this.images[index].prompt || '',
            isGenerating: false,
          }
          this.$set(this.images, index, updatedCard)
          this.currentGeneratingCardId = null
          return
        }
      }

      // Fallback: Remove any generating cards and add new generated card
      this.images = this.images.filter(img => !img.isGenerating)
      this.addImageToGrid({
        id: `generated-${Date.now()}`,
        url: imageUrl,
        badgeLabel: 'Generated',
        prompt: promptText || '',
        isGenerating: false,
        isLoadingPrompt: false,
        isGeneratingVideo: false,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.image-generation {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>
